import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import type { Result } from '../types/result';

/**
 * Programs the user has successfully submitted a lead for through the
 * governed DSL lead flow (`LeadFlowProvider.openLeadFlow`). Exposes that
 * submitted state back to the DSL render path so cards / approved
 * sections can reflect it read-only and the runtime can govern
 * duplicate-school attempts.
 *
 * The provider owns the canonical submitted list (full `Result[]` so the
 * duplicate "view next steps" path can reopen the governed flow without a
 * refetch) and derives a JSON-serializable {@link SubmittedProgramsSnapshot}
 * of the aggregate `programIds` / `schoolIds` / `count` that DSL `_bind`
 * expressions can address.
 *
 * # Pattern
 *
 * This mirrors `ComparisonProvider` exactly — refs for stable callbacks,
 * a memoized JSON-safe snapshot published into `flowContext` by
 * `ConfigurableStep`, and a tolerant `useSubmittedProgramsOptional()`
 * variant so infrastructure code (and standalone tests) can run with or
 * without the provider in scope. The duplicate-school business rule is
 * owned by `LeadFlowProvider` runtime code, NOT authorable in DSL (the
 * SAD "no arbitrary behavior in DSL" boundary).
 *
 * # Data binding contract
 *
 * The snapshot shape is intentionally JSON-serializable and contains
 * only stable aggregate fields so no function-call grammar is needed in
 * the binding resolver:
 *
 * ```ts
 * {
 *   programIds: number[],
 *   schoolIds: string[],
 *   count: number,
 * }
 * ```
 *
 * `_bind` interpolation only supports flat, dot-separated paths of plain
 * identifier segments — it rejects nested `{{ ... }}` and non-identifier
 * (e.g. numeric) path segments. So authors can only bind the stable
 * aggregate fields, e.g.:
 * `_bind.<prop>: "{{context.submissions.count}}"`
 * `_bind.<prop>: "{{context.submissions.programIds}}"`
 *
 * A per-program submitted check would need a map keyed by a dynamic
 * `item.programId`, but that numeric-segment lookup is NOT expressible in
 * DSL `_bind` (the resolver rejects non-identifier path segments), so no
 * such map is published. Cards derive that read-only state in runtime
 * code via the {@link useSubmittedForProgram} hook instead.
 */
export interface SubmittedProgramsSnapshot {
  readonly programIds: readonly number[];
  readonly schoolIds: readonly string[];
  readonly count: number;
}

/** Light, JSON-safe accessor for `ProgramSelect` / `Spotlight`. */
export interface SubmittedResultRef {
  readonly programId: number;
  readonly schoolId: string;
}

export interface SubmittedProgramsContextValue {
  /** Snapshot suitable for publication into `flowContext` for `_bind`. */
  readonly snapshot: SubmittedProgramsSnapshot;
  /** Record a successful submission. Dedupes by `programId`. */
  trackSubmission: (result: Result) => void;
  /** True when a lead was submitted for this `programId`. */
  isProgramSubmitted: (programId: number) => boolean;
  /** True when a lead was submitted for any program at this `schoolId`. */
  hasSchoolSubmitted: (schoolId: string) => boolean;
  /**
   * The full `Result` already submitted for a school, if any — used by
   * the governed duplicate "view next steps" path to reopen the flow
   * without a refetch.
   */
  getSubmittedForSchool: (schoolId: string) => Result | undefined;
  /**
   * Light `{ programId, schoolId }[]` accessor for the presentational
   * popup surfaces (`ProgramSelect` / `Spotlight`).
   */
  readonly submittedResults: readonly SubmittedResultRef[];
  /**
   * The full submitted `Result[]`, in submission order. Surfaces that
   * need the complete program/school payload (e.g. the `Submissions`
   * recap on the DSL render path) read this instead of the light
   * `submittedResults` ref list.
   */
  readonly submittedResultsFull: readonly Result[];
}

const SubmittedProgramsContext = createContext<
  SubmittedProgramsContextValue | undefined
>(undefined);

export interface SubmittedProgramsProviderProps {
  children: ReactNode;
}

export const SubmittedProgramsProvider: FC<SubmittedProgramsProviderProps> = ({
  children,
}) => {
  // Store the full `Result` so the duplicate "view next steps" path can
  // reopen the governed flow without a refetch.
  const [submitted, setSubmitted] = useState<Result[]>([]);

  // Dedupe inside the updater (not against a ref) so two submissions of
  // the same program queued in the same React 18 batch still collapse to
  // one entry — a closure-captured pre-check would race the batched
  // scheduler and double-record.
  const trackSubmission = useCallback((result: Result) => {
    setSubmitted((prev) => {
      if (prev.some((r) => r.programId === result.programId)) {
        return prev;
      }
      return [...prev, result];
    });
  }, []);

  const isProgramSubmitted = useCallback(
    (programId: number): boolean =>
      submitted.some((r) => r.programId === programId),
    [submitted],
  );

  const hasSchoolSubmitted = useCallback(
    (schoolId: string): boolean =>
      submitted.some((r) => r.schoolId === schoolId),
    [submitted],
  );

  const getSubmittedForSchool = useCallback(
    (schoolId: string): Result | undefined =>
      submitted.find((r) => r.schoolId === schoolId),
    [submitted],
  );

  const submittedResults = useMemo<readonly SubmittedResultRef[]>(
    () =>
      submitted.map((r) => ({
        programId: r.programId,
        schoolId: r.schoolId,
      })),
    [submitted],
  );

  const snapshot = useMemo<SubmittedProgramsSnapshot>(() => {
    const programIds = submitted.map((r) => r.programId);
    const schoolIds = submitted.map((r) => r.schoolId);
    return {
      programIds,
      schoolIds,
      count: programIds.length,
    };
  }, [submitted]);

  const value = useMemo<SubmittedProgramsContextValue>(
    () => ({
      snapshot,
      trackSubmission,
      isProgramSubmitted,
      hasSchoolSubmitted,
      getSubmittedForSchool,
      submittedResults,
      submittedResultsFull: submitted,
    }),
    [
      snapshot,
      trackSubmission,
      isProgramSubmitted,
      hasSchoolSubmitted,
      getSubmittedForSchool,
      submittedResults,
      submitted,
    ],
  );

  return (
    <SubmittedProgramsContext.Provider value={value}>
      {children}
    </SubmittedProgramsContext.Provider>
  );
};

/**
 * Returns the active submitted-programs context — throws when called
 * outside a {@link SubmittedProgramsProvider} (mirrors `useComparison`).
 */
export const useSubmittedPrograms = (): SubmittedProgramsContextValue => {
  const context = useContext(SubmittedProgramsContext);
  if (context === undefined) {
    throw new Error(
      'useSubmittedPrograms must be used within a SubmittedProgramsProvider',
    );
  }
  return context;
};

/**
 * Tolerant variant — returns `undefined` when no provider is mounted.
 * Used by infrastructure code that runs across surfaces with and without
 * a submitted context (`LeadFlowProvider`, `ConfigurableStepBridge`,
 * `DslResultsPagePopups`, the `useSubmittedForProgram` card hook) so the
 * surrounding feature still works standalone (e.g. in tests).
 */
export const useSubmittedProgramsOptional = ():
  | SubmittedProgramsContextValue
  | undefined => useContext(SubmittedProgramsContext);
